import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Alert } from "antd"
import { useSelector } from "react-redux";

const Lean = ({ value, onChange, disabledValues = [] }) => {
  const [hoveredValue, setHoveredValue] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
  const containerRef = useRef(null);
  const incomeFocusValue = useSelector(state=>state.path.formData?.incomeFocus || 0);


  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const positions = [
    { value: -2, label: 'Strong Value', icon: TrendingDown, color: 'from-blue-500 to-cyan-400' },
    { value: -1, label: 'Value', icon: TrendingDown, color: 'from-blue-400 to-cyan-300' },
    { value: 0, label: 'Neutral', icon: Minus, color: 'from-purple-500 to-purple-400' },
    { value: 1, label: 'Growth', icon: TrendingUp, color: 'from-rose-400 to-pink-300' },
    { value: 2, label: 'Strong Growth', icon: TrendingUp, color: 'from-rose-500 to-pink-400' },
  ];

  const activePosition = positions.find(pos => pos.value === value) || positions[2];

  const getXPosition = (value) => {
    if (!containerWidth) return 0;
    const segmentWidth = containerWidth / 4;
    switch(value) {
      case -2: return -(segmentWidth * 1.25);
      case -1: return -(segmentWidth * 0.5);
      case 0: return 0;
      case 1: return (segmentWidth * 0.5);
      case 2: return (segmentWidth * 1);
      default: return 0;
    }
  };


  const handleClick = (positionValue) => {
    if (incomeFocusValue > 50 && positionValue > 0) {
      setWarningMessage("Growth style is not available with high income focus");
      setShowWarning(true);
      setTimeout(() => setShowWarning(false), 3000);
      return;
    }
    onChange(positionValue);
  };

  return (
    <div className="flex flex-col gap-8">
      {showWarning && (
        <Alert
          message="Option Unavailable"
          description={warningMessage}
          type="error"
          showIcon
          closable
          onClose={() => setShowWarning(false)}
        />
      )}

      <motion.div
        ref={containerRef}
        className="relative h-24 bg-gray-100 rounded-2xl overflow-hidden"
        initial={false}
      >
        <motion.div
          className={`absolute inset-0 bg-gradient-to-r ${activePosition.color} opacity-20`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 0.5 }}
        />

        <div className="absolute inset-0 flex justify-between items-center px-8">
          {positions.map((pos) => (
            <div key={pos.value} className="w-px h-8 bg-gray-300" />
          ))}
        </div>

        <motion.div
          className="absolute top-1/2 left-1/2 w-16 h-16"
          style={{ translateY: '-50%' }}
          animate={{
            x: getXPosition(value),
            rotate: (value || 0) * 15
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
        >
          <div className={`
            w-full h-full rounded-full bg-gradient-to-br ${activePosition.color}
            shadow-lg flex items-center justify-center text-white
          `}>
            <activePosition.icon className="w-8 h-8" />
          </div>
        </motion.div>
      </motion.div>

      <div className="grid grid-cols-5 gap-3">
        {positions.map((position) => {
          const isActive = value === position.value;
          const isHovered = hoveredValue === position.value;
          const isDisabled = disabledValues.includes(position.value);
          const Icon = position.icon;

          return (
            <motion.div
              key={position.value}
              className={`
                relative cursor-pointer rounded-xl overflow-hidden
                ${isActive ? 'ring-2 ring-offset-2' : 'hover:ring-1 hover:ring-offset-1'}
                ring-offset-white ring-gray-200
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onHoverStart={() => !isDisabled && setHoveredValue(position.value)}
              onHoverEnd={() => setHoveredValue(null)}
              onClick={() => handleClick(position.value)}
              whileHover={!isDisabled ? { y: -4 } : {}}
              animate={{
                scale: isActive ? 1.05 : 1,
              }}
            >
              <div className={`
                p-4 h-full flex flex-col items-center justify-center gap-2
                ${isActive || isHovered ? `bg-gradient-to-br ${position.color} text-white` : 'bg-white'}
                transition-colors duration-200
              `}>
                <Icon className={`w-6 h-6 ${isActive || isHovered ? 'text-white' : 'text-gray-600'}`} />
                <div className="text-sm font-medium text-center">
                  {position.label}
                </div>
              </div>

              <AnimatePresence>
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-white mix-blend-soft-light"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.15 }}
                    exit={{ opacity: 0 }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        className="text-center text-gray-600"
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-sm">
          {value === -2 && "Strongly favoring value investments with emphasis on fundamentals and margin of safety"}
          {value === -1 && "Leaning towards value investments while maintaining some flexibility"}
          {value === 0 && "Balanced approach considering both value and growth opportunities"}
          {value === 1 && "Favoring growth potential while maintaining reasonable valuations"}
          {value === 2 && "Strong focus on high-growth opportunities and future potential"}
        </p>
      </motion.div>
    </div>
  );
};

export default Lean;